import type {
  ClientGroup,
  ClientPostTypeEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useMemo, type FC } from 'react';
import { StyleSheet, View } from 'react-native';
import type { TextProps } from 'react-native-paper';
import { Text } from 'react-native-paper';

import { GroupsIcon } from '@/assets';
import { useGetPostTypeFeature } from '@/features/post-editor/hooks/useGetPostFeature';
import {
  lableButtonStyleButton,
  lableButtonStyleLabel
} from '@/features/posts/styles';
import { postCardInteractiveStyle } from '@/features/posts/styles/postCardPointer';
import { PostFeedType } from '@/features/posts/types/postFeed';
import {
  POST_TYPE_POST_FEED_TYPE,
  POST_TYPE_ICONS,
  POST_FEED_TYPE_MEIN_ORT_CHANNEL,
  POST_FEED_TYPE_MEIN_ORT_TAB
} from '@/features/posts/utils/postFeed';
import { NavigationButton } from '@/navigation/components/NavigationButton';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    chip: { minHeight: 20 },
    labelChip: {
      marginVertical: 0,
      color: theme.colors.primary
    },
    chipButton: { marginLeft: 8 },
    button: lableButtonStyleButton,
    label: { color: theme.colors.primary }
  });

type PostTypeButtonProps = {
  postType: ClientPostTypeEnum | PostFeedType;
  asChip?: boolean;
  labelVariant?: TextProps<string>['variant'];
  group?: ClientGroup;
};

export const PostTypeButton: FC<PostTypeButtonProps> = ({
  postType,
  group,
  asChip = false,
  labelVariant
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const feature = useGetPostTypeFeature(postType);

  const label = group ? group.name : feature.channelName;

  const navigationParams = useMemo(() => {
    if (group) {
      return {
        screen: AppRoute.GroupDetail,
        params: { groupId: group.id, ...group }
      };
    }
    if (postType === PostFeedType.CIVIL_PROTECTION) {
      return {
        screen: AppRoute.CivilProtection
      };
    }
    const feedType = POST_TYPE_POST_FEED_TYPE[postType];
    return {
      screen: AppRoute.Tabs,
      params: {
        screen: TabRoute.Radio,
        params: {
          channel: POST_FEED_TYPE_MEIN_ORT_CHANNEL[feedType],
          tab: POST_FEED_TYPE_MEIN_ORT_TAB[feedType]
        }
      }
    };
  }, [group, postType]);

  return (
    <View style={postCardInteractiveStyle}>
      <NavigationButton
        {...navigationParams}
        mode={asChip ? 'contained' : 'outlined'}
        compact
        buttonColor={asChip ? theme.colors.tertiaryContainer : undefined}
        style={asChip ? styles.chipButton : styles.button}
        contentStyle={asChip ? styles.chip : undefined}
        labelStyle={asChip ? styles.labelChip : lableButtonStyleLabel}
        icon={
          asChip
            ? undefined
            : () => {
                if (group) {
                  return (
                    <GroupsIcon
                      fill={theme.colors.primary}
                      width={18}
                      height={18}
                    />
                  );
                }

                return POST_TYPE_ICONS[postType]({
                  fill: theme.colors.primary,
                  width: 18,
                  height: 18
                });
              }
        }
        theme={{ colors: { outline: theme.colors.outlineVariant } }}
      >
        <Text
          variant={labelVariant}
          style={[asChip ? styles.labelChip : styles.label]}
        >
          {label}
        </Text>
      </NavigationButton>
    </View>
  );
};
